import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { showToast } from "../errors";
import { useNow } from "../osr/useCountdown";
import OsrPhotos from "../osr/OsrPhotos";
import { fetchWithAuth } from "../../utils/api";
import { failureText, webChatsApi } from "./webChatsApi";

/**
 * The live side of one website chat, for staff (Stage 2 and "A chat's life"; docs/WEB_CHAT.md; chariot-backend
 * services/webChat/live.js, lifecycle.js):
 *   VisitorPresence  the visitor's dot — active (the chat is showing), away (another tab or app), left — and typing
 *   LiveBanner       the visitor is waiting for a person (Accept, with the time left), I have the chat (End live
 *                    chat; their dot is in the panel's head), or someone else has it
 *   ChatMessages     the chat as it happened: the visitor (their photos and files too, and what came by email), Hank,
 *                    the team member, Chariot's notes (web_chat_message)
 *   ReplyBox         my reply while I have the chat. Enter sends; Shift+Enter starts a new line. My typing shows them a
 *                    typing bubble; while they are not there it says the message will be emailed if unseen
 *   EmailReply       an answer for a chat nobody has live (waiting on us, or answered by email): it goes by email now
 */
const TEAM_WORD = { sales: "Sales", customer_service: "Customer service", billing: "Billing", poseidon_plus: "Poseidon Plus" };
const PRESENCE = {
  active: { dot: "on", words: "Active now" },
  away: { dot: "away", words: "Away: another tab or app" },
  left: { dot: "off", words: "Left the chat" },
};
const TYPING_PING_MS = 3000;

/** "1:42 left" for a hand-off's deadline, or null once it has passed. Shared with LiveChatAlerts.js. */
export function timeLeft(deadline, now) {
  const ms = new Date(deadline).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")} left`;
}

export function VisitorPresence({ chat }) {
  const p = PRESENCE[chat.presence] || PRESENCE.left;
  const name = chat.first_name || "They";
  return (
    <span className="wcs-presence" data-state={p.dot}>
      <span className="wcs-presence-dot" aria-hidden="true" />
      {chat.visitor_typing && chat.presence === "active" ? `${name} is typing…` : p.words}
    </span>
  );
}

export function LiveBanner({ chat, me, canTake, onChanged }) {
  const [busy, setBusy] = useState(false);
  const now = useNow(1000, chat.live_status === "waiting");
  const visitor = chat.first_name || "The visitor";
  const run = async (work, done) => {
    setBusy(true);
    try {
      await work();
      if (done) showToast.success(done);
    } catch (err) {
      showToast.error(failureText(err));
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  if (chat.live_status === "waiting") {
    const left = timeLeft(chat.live_deadline_at, now);
    const leftWords = left ? `${left} to accept` : "Hank takes it back any moment now";
    return (
      <div className="wcs-block wcs-block-waiting" role="status">
        <div className="wcs-label">{visitor} wants a person{chat.live_team ? ` · ${TEAM_WORD[chat.live_team] || chat.live_team}` : ""}</div>
        {chat.live_about && <p className="wcs-text">{chat.live_about}</p>}
        <span>{leftWords}. If nobody accepts, Hank saves the request for the team.</span>
        {canTake && (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => run(() => webChatsApi.accept(chat.id), "You joined the chat")}>
            {busy ? "Joining…" : "Accept"}
          </button>
        )}
      </div>
    );
  }
  if (chat.live_status !== "person") return null;
  if (String(chat.live_user_id) !== String(me)) {
    return <p className="wcs-muted">{chat.live_with || "Someone from the team"} is chatting with them now.</p>;
  }
  return (
    <div className="wcs-block wcs-block-live" role="status">
      <div className="wcs-label">You're chatting with {visitor}</div>
      <span>Hank is out of this chat until you end it.</span>
      <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => run(() => webChatsApi.endLive(chat.id), "Live chat ended — Hank answers again")}>
        {busy ? "Ending…" : "End live chat"}
      </button>
    </div>
  );
}

const size = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`);
const fileUrl = (chatId, fileId) => `/admin/web-chat/chats/${encodeURIComponent(chatId)}/files/${encodeURIComponent(fileId)}`;

/** A document the visitor sent: fetched with my sign-in, then saved. */
function FileLink({ chatId, file }) {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const res = await fetchWithAuth(fileUrl(chatId, file.id), { skipSaveIndicator: true });
      if (!res.ok) throw new Error(`file ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name || "file";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      showToast.error("That file didn't download. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="wcs-file" onClick={save} disabled={busy}>
      <span className="wcs-file-name">{file.name}</span>
      <span className="wcs-file-meta">{busy ? "Downloading…" : `${size(file.size)} · Download`}</span>
    </button>
  );
}

const WHO = { visitor: null, hank: "Hank", system: null };

export function ChatMessages({ messages, visitorName, chatId }) {
  return (
    <section className="wcs-session" aria-label="The chat">
      {messages.map((m, i) => {
        if (m.role === "system") return <p key={m.id} className="wcs-system">{m.text}</p>;
        const side = m.role === "visitor" ? "user" : "agent";
        const name = m.role === "staff" ? m.by || "Team member" : m.role === "visitor" ? visitorName : WHO[m.role];
        const named = !messages[i - 1] || messages[i - 1].role !== m.role || messages[i - 1].by !== m.by;
        return (
          <div key={m.id} className={`wcs-turn wcs-turn-${side}${m.role === "staff" ? " wcs-turn-staff" : ""}`}>
            {named && name && <div className="wcs-turn-who">{name}</div>}
            {m.file?.kind === "image" && <OsrPhotos images={[m.file.id]} urlFor={(id) => fileUrl(chatId, id)} alt={`Photo from ${visitorName}`} emptyText="" />}
            {m.file && m.file.kind !== "image" && <FileLink chatId={chatId} file={m.file} />}
            {!m.file && <div className="wcs-turn-text">{m.text}</div>}
            {m.via === "email" && <div className="wcs-turn-tools">by email</div>}
          </div>
        );
      })}
    </section>
  );
}

/** A box that grows with what is written; Enter sends, Shift+Enter starts a new line. */
function useGrowingBox(draft) {
  const box = useRef(null);
  useEffect(() => { box.current?.focus(); }, []);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);
  return box;
}

export function ReplyBox({ chatId, visitorName, onSent, presence = "active", delivery = null }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const box = useGrowingBox(draft);
  const lastPing = useRef(0);

  const change = (value) => {
    setDraft(value);
    // They see me typing: at most one word to Chariot every few seconds.
    if (value.trim() && Date.now() - lastPing.current > TYPING_PING_MS) {
      lastPing.current = Date.now();
      webChatsApi.typing(chatId).catch(() => {});
    }
  };
  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const body = await webChatsApi.reply(chatId, text);
      setDraft("");
      lastPing.current = 0;
      onSent(body.message);
    } catch (err) {
      showToast.error(failureText(err));
    } finally {
      setBusy(false);
      box.current?.focus();
    }
  };
  const away = presence !== "active";
  return (
    <form className="wcs-reply" onSubmit={send}>
      <label htmlFor="wcs-reply" className="wcs-label">Reply to {visitorName}</label>
      {away && (
        <p className="wcs-reply-hint" role="status">
          {visitorName} isn't in the chat right now.
          {delivery?.email_unseen ? ` If they don't see your message within ${delivery.unseen_email_minutes} minute${delivery.unseen_email_minutes === 1 ? "" : "s"}, it's emailed to them.` : " They'll see it when they come back."}
        </p>
      )}
      <textarea
        id="wcs-reply"
        ref={box}
        className="wcs-note"
        rows={2}
        maxLength={2000}
        placeholder="Write a message…"
        value={draft}
        onChange={(e) => change(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) send(e); }}
      />
      <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !draft.trim()}>{busy ? "Sending…" : "Send"}</button>
    </form>
  );
}

const EMAILED = {
  sent: (email) => `Emailed to ${email}`,
  dry: () => "Saved. Emails go out only from production",
  off: () => "Saved, but the email is switched off (Settings ▸ Message Templates)",
  failed: () => "Saved, but the email didn't go out. Try again in a moment",
  none: () => "Saved",
};

export function EmailReply({ chat, onSent }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  useEffect(() => { if (open) box.current?.focus(); }, [open]);
  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const body = await webChatsApi.emailReply(chat.id, text);
      setDraft("");
      setOpen(false);
      const words = (EMAILED[body.emailed] || EMAILED.none)(chat.email);
      if (body.emailed === "failed") showToast.error(words); else showToast.success(words);
      onSent(body.message);
    } catch (err) {
      showToast.error(failureText(err));
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <div className="wcs-reply wcs-reply-closed">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>Answer by email</button>
      </div>
    );
  }
  return (
    <form className="wcs-reply wcs-email-reply" onSubmit={send}>
      <label htmlFor="wcs-email-reply" className="wcs-label">Answer by email</label>
      <span className="wcs-muted">Goes to {chat.email} now, and stays in this chat. {chat.first_name || "They"} can answer by email or back in the chat.</span>
      <textarea
        id="wcs-email-reply"
        ref={box}
        className="wcs-note"
        rows={3}
        maxLength={2000}
        placeholder="Write your answer…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="wcs-reply-actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !draft.trim()}>{busy ? "Sending…" : "Send by email"}</button>
      </div>
    </form>
  );
}
